import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react'
import type { ReactNode } from 'react'
import { CheckOutlined } from '../icons'

export type ToastTone = 'success' | 'error' | 'info'

/** Almost every toast in the app is bottom-center (interactions.md §5's
 * `flash(msg)`) and stays that way by default. `'top'` exists ONLY for the
 * upload flow's "N parts parsed from …" notice (UID-509 follow-up), which
 * fires alongside the "Add these files to a project" modal and needs to sit
 * above it, matching the design's own single global toast
 * (`Fictiv Platform.dc.html:179`, `top:24px`) — that design toast has no
 * OTHER caller in this app, so this is deliberately a per-call opt-in, not a
 * change to the shared stack's default. */
export type ToastPosition = 'top' | 'bottom'

interface ToastRecord {
  readonly id: number
  readonly message: string
  readonly tone: ToastTone
  readonly position: ToastPosition
}

interface ToastContextValue {
  /** Shows a transient toast (error kit — components.md "Toast" /
   * interactions.md §5's `flash(msg)`), bottom-center by default. Auto-
   * dismisses after 4s (interactions.md §5's recommendation, since the
   * source doesn't specify one) unless the caller keeps triggering new
   * toasts. */
  readonly showToast: (message: string, tone?: ToastTone, position?: ToastPosition) => void
}

const ToastContext = createContext<ToastContextValue | undefined>(undefined)

const AUTO_DISMISS_MS = 4000

/**
 * App-wide toast host (error kit). Wrap the app once, near the root — every
 * screen calls `useToast().showToast(...)` instead of inventing its own
 * transient-message UI. `role="status"` + `aria-live="polite"` (per
 * components.md "Toast") so assistive tech announces it without stealing
 * focus, and a manual dismiss button so a longer message doesn't vanish
 * before it's read (interactions.md §5).
 */
export function ToastProvider({ children }: { readonly children: ReactNode }) {
  const [toasts, setToasts] = useState<readonly ToastRecord[]>([])
  const nextId = useRef(0)
  const timers = useRef<Map<number, ReturnType<typeof setTimeout>>>(new Map())

  const dismiss = useCallback((id: number) => {
    setToasts((current) => current.filter((toast) => toast.id !== id))
    const timer = timers.current.get(id)
    if (timer) {
      clearTimeout(timer)
      timers.current.delete(id)
    }
  }, [])

  const showToast = useCallback(
    (message: string, tone: ToastTone = 'success', position: ToastPosition = 'bottom') => {
      const id = nextId.current++
      setToasts((current) => [...current, { id, message, tone, position }])
      const timer = setTimeout(() => dismiss(id), AUTO_DISMISS_MS)
      timers.current.set(id, timer)
    },
    [dismiss],
  )

  // Auto-dismiss timers outlive a single render and are NOT cleaned up by
  // `dismiss` when a toast is cleared by unmount instead of by its own timer
  // firing — a test (or a real navigation away) can unmount this provider
  // while a toast is still pending, and the orphaned `setTimeout` then fires
  // `dismiss` -> `setState` against a torn-down environment (`window is not
  // defined` under jsdom). Clear every outstanding timer on unmount.
  useEffect(() => {
    return () => {
      for (const timer of timers.current.values()) {
        clearTimeout(timer)
      }
      timers.current.clear()
    }
  }, [])

  const value = useMemo<ToastContextValue>(() => ({ showToast }), [showToast])

  const renderStack = (position: ToastPosition, className: string) => (
    // `<output>` carries an implicit role="status" (HTML-AAM) — the
    // semantic element biome's a11y rule prefers over a bare
    // role="status" div; aria-live is kept explicit since it's still
    // the clearest signal for screen readers regardless of the
    // element's implicit role.
    <output className={className} aria-live="polite">
      {toasts
        .filter((toast) => toast.position === position)
        .map((toast) => (
          <div key={toast.id} className={`toast toast-${toast.tone}`}>
            <CheckOutlined aria-hidden="true" />
            <span className="toast-message">{toast.message}</span>
            <button
              type="button"
              className="toast-dismiss"
              aria-label="Dismiss notification"
              onClick={() => dismiss(toast.id)}
            >
              &times;
            </button>
          </div>
        ))}
    </output>
  )

  // The bottom stack is ALWAYS rendered (matches every caller's pre-existing
  // expectation of exactly one `role="status"` region when nothing top-
  // positioned is in play). The top stack only mounts once something has
  // actually asked for it — an always-present second `<output>` would be a
  // second `role="status"` region on every screen that never uses `'top'`.
  const hasTopToast = toasts.some((toast) => toast.position === 'top')

  return (
    <ToastContext.Provider value={value}>
      {children}
      {renderStack('bottom', 'toast-stack')}
      {hasTopToast && renderStack('top', 'toast-stack-top')}
    </ToastContext.Provider>
  )
}

export function useToast(): ToastContextValue {
  const context = useContext(ToastContext)
  if (!context) {
    throw new Error('useToast() must be used within a <ToastProvider>')
  }
  return context
}
